<template>
    <KCard
        :as="as"
        :variant="variant"
        :padding="padding"
        :class="classes"
    >
        <KSplit
            :aside-position="asidePosition"
            :align="align"
            :stack="stack"
            :overlay-wide="overlayWide"
            :container="container"
            :class="element('layout')"
        >
            <div :class="element('body')">
                <component
                    :is="titleAs"
                    v-if="$slots.title || title"
                    class="k-heading-2"
                    :class="[element('title')]"
                >
                    <slot name="title">
                        {{ title }}
                    </slot>
                </component>

                <div
                    v-if="$slots.default || text"
                    class="k-text-lg k-prose"
                    :class="[element('text')]"
                >
                    <slot>{{ text }}</slot>
                </div>

                <div
                    v-if="$slots.actions"
                    :class="element('actions')"
                >
                    <slot name="actions" />
                </div>
            </div>

            <template
                v-if="$slots.aside"
                #aside
            >
                <slot name="aside" />
            </template>
        </KSplit>
    </KCard>
</template>

<script setup lang="ts">
import type { KContentBlockProps } from './types'
import useBem from '@yeeroen/use-bem'

import { computed } from 'vue'
import KCard from '../card/k-card.vue'

import KSplit from '../split/k-split.vue'

defineOptions({ name: 'KContentBlock' })

const props = withDefaults(defineProps<KContentBlockProps>(), {
    as: 'section',
    titleAs: 'h2',
    asidePosition: 'end',
    align: 'center',
    stack: 'aside-first',
    overlayWide: false,
    variant: 'plain',
    container: false,
})

defineSlots<{
    default?: () => unknown
    aside?: () => unknown
    title?: () => unknown
    actions?: () => unknown
}>()

const { block, element, modifier } = useBem('k-content-block')

const classes = computed(() => block(
    modifier('stack-overlay', props.stack === 'overlay'),
    modifier('overlay-wide', props.stack === 'overlay' && props.overlayWide),
))
</script>

<style lang="scss">
@use './k-content-block.scss';
</style>
